import { IParameterDto } from '../../__legacy__/helpers/parameters.helpers';
import { ImageSetType } from '../../__legacy__/modules/projectDetails/sub/images/projectDetailsImages.model';
import { IJobAnnotationTool, IJobStartingProgress, JOB_ANNOTATION_TYPE, JOB_OWNERSHIP, JOB_STATUS, JOB_TYPE, TRAINING_VARIANT } from './jobs.model';

export interface IThresholdConfig {
  max_score: number;
  min_score: number;
  optimal_threshold: number;
  threshold_step: number;
  min_threshold: number | null;
  max_threshold: number | null;
}

interface IJobDetailsDtoBase {
  id: string;
  config: {
    date_created: string;
    status: JOB_STATUS;
    datasets: {
      project_id: string | null;
      datasets_ids: string[];
    };
    inference_config_: object;
    is_owner: boolean;
  };
  threshold: null | IThresholdConfig;
  username: string;
}

export type ITrainingDetailsDto = IJobDetailsDtoBase & {
  annotation_tools: string[];
  config: {
    model_name: string;
    model_description: string;
    model_variant: TRAINING_VARIANT;
    datasets: {
      project_id: string;
    };
    model_config_: object;
  };
  is_auto_training: boolean;
  parent_job_id: string | null;
};

export type IInferenceDetailsDto = IJobDetailsDtoBase & {
  training_job_id: string;
  model_variant: TRAINING_VARIANT;
  config: {
    inference_name: string;
    inference_description: string | null;
    datasets: {
      project_id: string | null;
    };
    inference_config_: object;
    is_owner: boolean;
    is_training_owner: boolean;
  };
};

interface Point2D {
  x: number;
  y: number;
}

interface IInferenceResult {
  name: string;
  train: number | string | null;
  validation: number | string | null;
  test: number | string | null;
}

interface IConfusionMatrix {
  data: Array<{
    id: string;
    name: string;
    [k: string]: string | number;
  }>;
  fields: Array<{ label: string; value: string }>;
}

export interface IJobMetricsDto {
  train_loss: Point2D[] | null;
  train_score: Point2D[] | null;
  validation_score: Point2D[] | null;
  metrics: {
    confusion_matrix: IConfusionMatrix | null;
    precision_recall_curve: {
      train: Point2D[] | null;
      validation: Point2D[] | null;
      test: Point2D[] | null;
    } | null;
    results: IInferenceResult[];
    auc: number | null;
  };
}

export interface IJobStatusDto {
  status: JOB_STATUS;
  cost: number;
  starting_progress: IJobStartingProgress;
  job_progress: IJobMetricsDto;
  additional_info: string[];
  training_time: number;
  failure_reason: string | null;
}

export interface IJobSettingsDto {
  params: IParameterDto[];
}

export const INTEGER_RESULT_NAMES = ['count'] as const;

export interface IJobImageDto {
  id: string;
  name: string;
  imageSet?: ImageSetType;
  url: string;
}

interface IJobImageMetricsDto {
  confusion_matrix: IConfusionMatrix | null;
  recall: number | null;
  precision: number | null;
  f1: number | null;
  auc: number | null;
  count: number | null;
  precision_recall_curve: Point2D[] | null;
  score: number | null;
  average_entropy: number | null;
  confidence: number | null;
}

interface IPredictedPoint extends Point2D {
  score: number;
}

interface IPredictedVertice {
  lat: number;
  lng: number;
}

interface IPredictedRectangle {
  score: number;
  vertices: IPredictedVertice[];
}

interface IPredictedClass {
  class_id: string;
  points: IPredictedPoint[];
}

interface IPredictedRectanglesClass {
  class_id: string;
  rectangles: IPredictedRectangle[];
}

export interface IJobImagePredictionDto {
  ground_truth: {
    class_name: string | null;
    prediction_counts: Record<string, number> | null;
  };
  heatmap_id: string | null;
  mask_id: string | null;
  metrics: IJobImageMetricsDto;
  prediction: string | null;
  predicted_points: IPredictedClass[] | null;
  predicted_rectangles: IPredictedRectanglesClass[] | null;
}

export type IJobImagePredictionsDto = Record<string, IJobImagePredictionDto>;

interface IJobListItemDtoBase {
  cost_calculated: number | null;
  date_created: string;
  model_name: string;
  model_variant: TRAINING_VARIANT;
  project_name: string;
  score: number | null;
  status: JOB_STATUS;
  starting_progress: IJobStartingProgress;
  user_id: string;
  is_owner: boolean;
  failure_reason: string | null;
}

export interface ITrainingListItemDto extends IJobListItemDtoBase {
  jobType: JOB_TYPE.TRAINING;
  job_id: string;
  progress: string;
  is_auto_training: boolean;
  training_time: number;
}
export interface IInferenceListItemDto extends IJobListItemDtoBase {
  jobType: JOB_TYPE.INFERENCE;
  is_training_owner: boolean;
  training_job_id: string;
  inference_job_id: string;
  inference_name: string;
}

interface IThresholdDiscrepancyDto {
  base_min_threshold: number;
  base_max_threshold: number;
  current_min_threshold: number;
  current_max_threshold: number;
}

export type IJobThresholdDiscrepancyDto = IThresholdDiscrepancyDto | null;

export interface IJobActionDto {
  job_id: string;
}

export interface IJobEstimatedCostDto {
  cost_estimated: number;
}

interface IPagedDto {
  pageNumber: number;
  pageSize: number;
  orderBy: string;
  orderType: string;
}

export interface IJobsRequestDto extends IPagedDto {
  ownership: JOB_OWNERSHIP;
}

export interface IJobThresholdDto {
  min_threshold: number;
  max_threshold: number;
}

interface ISubTrainingJobParamDto {
  name: string;
  value: string;
}

export interface ISubTrainingJobDto {
  job_id: string;
  date_created: string;
  model_name: string;
  progress: string;
  score: number | null;
  status: JOB_STATUS;
  training_time: number;
  is_best: boolean;
  average_inference_time: number;
  params: ISubTrainingJobParamDto[];
}

export interface IVerifyAnnotationToolsDto {
  id: string;
  tool: JOB_ANNOTATION_TYPE;
  name: string;
  type: string;
}

export interface IInferenceStartDto {
  inference_name: string;
  inference_description?: string;
  inference_config_: Record<string, unknown>;
  datasets: {
    project_id?: string;
    datasets_ids: string[];
  };
}
export interface ITrainingStartDto {
  model_config_: Record<string, unknown>;
  model_variant: string;
  model_name: string;
  model_description: string;
  datasets: {
    project_id: string;
    datasets_ids: string[];
  };
  annotation_tools: IJobAnnotationTool[];
}

export type IJobModelsDto = Array<{ job_id: string; model_name: string }>;
export interface IJobProjectDto {
  id: string;
  name: string;
  modelVariant: TRAINING_VARIANT | null;
  sizeInMB: number;
}
export interface IProjectImagesDto extends IPagedDto {
  search?: string;
  projectId: string | null;
  datasetsIds: string[];
}

export interface IDatasetImagesDto extends IPagedDto {
  search?: string;
  datasetId: string;
}

export interface IWorkspaceDto {
  workspaceId: string;
}

export interface IProjectDto {
  projectId: string;
}

export interface IJobDatasetDto {
  id: string;
  name: string;
  createdDate: string;
  imagesCount: number;
  description: string;
  sizeInMB: number;
}

export interface IProjectDatasetDto extends IJobDatasetDto {
  testImagesCount: number;
  trainImagesCount: number;
  validationImagesCount: number;
}

export interface IInfoEditDto {
  name?: string;
  description?: string;
}

export interface IJobDownloadTokenDto {
  jwt: string;
}
